/**
 * Minimal, safe comment-markdown tokenizer (job-collaboration epic).
 *
 * Supported subset (DEC-JC-005): links `[text](http(s)://…|mailto:…)`,
 * **bold**, *italic* (`*text*` or `_text_`), `` `inline code` ``, `- `/`* `
 * bulleted lists, and paragraphs. Mentions are stored as `@[Name](user:ID)`
 * and surface as a dedicated token so the renderer can chip them.
 *
 * Deliberately flat: formatting does not nest (`**bold *x* **` degrades to
 * plain text inside the bold). This keeps the parser small and the output
 * predictable; the renderer maps tokens to React elements, so nothing is ever
 * interpreted as HTML — unknown input can only render as literal text.
 */

/** A parsed inline token; `value`/`text` content is always plain text. */
export type InlineToken =
  | { type: "text"; value: string }
  | { type: "code"; value: string }
  | { type: "bold"; value: string }
  | { type: "italic"; value: string }
  | { type: "link"; href: string; text: string }
  | { type: "mention"; userId: string; name: string };

/** A block: a paragraph (soft line breaks preserved) or a bulleted list. */
export type MarkdownBlock =
  | { type: "paragraph"; lines: InlineToken[][] }
  | { type: "list"; items: InlineToken[][] };

/**
 * One combined scan for every inline construct. Alternation order matters:
 * code first (its content must stay literal), then mentions, links, bold,
 * then italic — so `**bold**` is never read as two `*i*` runs. Links only
 * accept http(s)/mailto schemes, and `_italic_` requires non-word neighbours
 * so snake_case words survive untouched.
 */
const INLINE_PATTERN =
  /(`[^`\n]+`)|(@\[([^\]]*)\]\(user:([^)\s]+)\))|(\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\))|(\*\*([^*\n]+)\*\*)|(\*([^*\n]+)\*)|((?<![A-Za-z0-9])_(?=\S)([^_\n]+)(?<=\S)_(?![A-Za-z0-9]))/g;

/**
 * Tokenizes one line of markdown into inline tokens. Unrecognised text (and
 * text between tokens) degrades to plain text tokens.
 *
 * @param {string} line
 * @returns {InlineToken[]}
 */
export function parseInline(line: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let cursor = 0;
  for (const match of line.matchAll(INLINE_PATTERN)) {
    const start = match.index ?? 0;
    if (start > cursor) tokens.push({ type: "text", value: line.slice(cursor, start) });
    cursor = start + match[0].length;

    if (match[1] !== undefined) {
      tokens.push({ type: "code", value: match[1].slice(1, -1) });
    } else if (match[2] !== undefined) {
      tokens.push({ type: "mention", userId: match[4], name: match[3] });
    } else if (match[5] !== undefined) {
      tokens.push({ type: "link", href: match[7], text: match[6] });
    } else if (match[8] !== undefined) {
      tokens.push({ type: "bold", value: match[9] });
    } else if (match[10] !== undefined) {
      tokens.push({ type: "italic", value: match[11] });
    } else if (match[12] !== undefined) {
      tokens.push({ type: "italic", value: match[13] });
    }
  }
  if (cursor < line.length) tokens.push({ type: "text", value: line.slice(cursor) });
  return tokens;
}

/**
 * Parses a comment body into blocks. Consecutive list lines collapse into one
 * list block; blank lines separate blocks; soft line breaks inside a paragraph
 * are preserved as distinct lines.
 *
 * @param {string} body
 * @returns {MarkdownBlock[]}
 */
export function parseCommentMarkdown(body: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  let paragraph: InlineToken[][] | null = null;
  let list: InlineToken[][] | null = null;

  const flushParagraph = () => {
    if (paragraph) {
      blocks.push({ type: "paragraph", lines: paragraph });
      paragraph = null;
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({ type: "list", items: list });
      list = null;
    }
  };

  for (const rawLine of body.split(/\r?\n/)) {
    const line = rawLine.trimEnd();
    const listItem = /^\s*[-*]\s+(.*)$/.exec(line);
    if (listItem) {
      flushParagraph();
      list ??= [];
      list.push(parseInline(listItem[1]));
      continue;
    }
    flushList();
    if (line.trim().length === 0) {
      flushParagraph();
      continue;
    }
    paragraph ??= [];
    paragraph.push(parseInline(line));
  }
  flushParagraph();
  flushList();
  return blocks;
}
